import { Injectable, OnDestroy, signal, inject } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastService } from '../toast-service/toast.service';
import { NavigationEnd, Router } from '@angular/router';
import { Employee, EmployeeRequest, EmployeeRoleType } from '../../_interfaces/employee.interface';
import { ConfirmationRemoveModalComponent } from '../../shared/components/modals/confirmation-remove-modal/confirmation-remove-modal.component';
import { EmployeeApiService } from './api/employee-api.service';
import { OffcanvasService } from '../offcanvas-service/offcanvas.service';
import { SelectOption } from '../../_interfaces/select-field.interface';
import { filter, map, Observable, Subscription } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class EmployeeService implements OnDestroy {
  readonly modalService = inject(NgbModal);
  readonly employeeApiService = inject(EmployeeApiService);
  readonly toastService = inject(ToastService);
  readonly router = inject(Router);
  readonly offcanvasService = inject(OffcanvasService);

  readonly subscriptions = new Subscription();
  protected returnUrl: string = '/employees';
  readonly inviteReturnUrl: string = '/employee-invitations';
  public conditionalReturnUrl = signal(this.returnUrl); // default

  constructor() {
    // react to route changes
    this.subscriptions.add(
      this.router.events
        .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
        .subscribe((event: NavigationEnd) => {
          this.handleRouteChange(event.urlAfterRedirects);
        }),
    );
    this.handleRouteChange(this.router.url);
  }

  handleRouteChange(url: string) {
    this.conditionalReturnUrl.set(url.includes('invitations') ? this.inviteReturnUrl : this.returnUrl);
  }

  onAddEmployee() {
    this.offcanvasService.offCanvasNavigate(this.conditionalReturnUrl() + '/add/employee-details');
  }

  onEditEmployee(id: string): void {
    this.offcanvasService.offCanvasNavigate(this.conditionalReturnUrl() + '/edit/' + id);
  }

  employeeAllOptions(): Observable<SelectOption[]> {
    return this.employeeApiService
      .getAllEmployees()
      .pipe(map((employees) => employees.map<SelectOption>((employee) => this.mapToSelectOption(employee))));
  }

  employeeAllOptionsUnmapped(): Observable<Employee[]> {
    return this.employeeApiService.getAllEmployees();
  }

  employeeTypeAdminOptions(): Observable<SelectOption[]> {
    return this.employeeApiService
      .getAllEmployees()
      .pipe(
        map((employees) =>
          employees
            .filter((emp) => emp.role === EmployeeRoleType.ADMIN)
            .map<SelectOption>((employee) => this.mapToSelectOption(employee)),
        ),
      );
  }

  mapToSelectOption(employee: Employee) {
    return {
      option: employee.first_name + ' ' + employee.last_name,
      value: employee.id,
    };
  }

  onRemoveEmployee(employee: Employee) {
    if (employee.is_approver) {
      // if the user is an approver, go to offcanvas steps to choose new approver
      this.offcanvasService.offCanvasNavigate(`employees/remove/${employee.id}`);
    } else {
      //Remove via modal if the user is not an approver
      this.removeEmployeePrompt(employee);
    }
  }

  removeEmployeePrompt(employee: Employee) {
    const messageStart = 'By removing this employee,';
    const messageEnd = 'will be removed as well.';
    const hasSubscriptionCard = employee.is_subscription_card_holder ? 'their subscription card(s)' : '';

    let removeMessage = '';

    if (employee.is_card_holder) {
      removeMessage = `${messageStart} their card ${hasSubscriptionCard ? 'and' : ''} ${hasSubscriptionCard} ${messageEnd}`;
    } else if (employee.is_subscription_card_holder) {
      removeMessage = `${messageStart} ${hasSubscriptionCard} ${messageEnd}`;
    }

    const modalRef = this.modalService.open(ConfirmationRemoveModalComponent, {
      centered: true,
      size: 'md',
    });
    modalRef.componentInstance.subject = `${employee?.first_name} ${employee?.last_name}`;
    modalRef.componentInstance.removeMessage = removeMessage;
    modalRef.closed.subscribe({
      next: () => {
        this.employeeApiService.removeEmployee(employee.id).subscribe({
          next: () => {},
          complete: () => {
            this.offcanvasService.close(this.returnUrl);
            this.toastService.show(`You have successfully removed an employee`, 'iconoir-check-circle');
          },
          error: () => {
            this.toastService.show('Something went wrong. This employee could not be removed.');
          },
        });
      },
    });
  }

  removeInvitationPrompt(employee: EmployeeRequest, empInvitationId: string) {
    const modalRef = this.modalService.open(ConfirmationRemoveModalComponent, {
      centered: true,
      size: 'md',
    });
    modalRef.componentInstance.subject = `${employee?.first_name} ${employee?.last_name}`;
    modalRef.componentInstance.removeMessage = `By removing the invitation, they won’t have access to Paystashio cards.`;
    modalRef.closed.subscribe({
      next: () => {
        this.employeeApiService.removeInvitedEmployee(empInvitationId).subscribe({
          next: () => {},
          complete: () => {
            this.offcanvasService.close(this.inviteReturnUrl);
            this.toastService.show(`Invitation removed.`, 'iconoir-check-circle');
          },
          error: () => {
            this.toastService.show('Something went wrong. This invitation could not be removed.');
          },
        });
      },
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
